/**
 * Client mirror of alternative-path resolution (keep aligned with modules/exploreUnknownAlternativePath.js).
 */
import {
  buildCompactAlternativeLayout,
  deriveAlternativeMissionIndices,
} from './exploreUnknownAlternativePathLayout.js';

const SURFACE_MISSION_SLICES = [
  { surface: 'arrival', firstMission: 1, lastMission: 5 },
  { surface: 'cave', firstMission: 6, lastMission: 10 },
  { surface: 'desert', firstMission: 11, lastMission: 16 },
  { surface: 'fair', firstMission: 17, lastMission: 23 },
  { surface: 'lavender', firstMission: 24, lastMission: 28 },
  { surface: 'treetop', firstMission: 29, lastMission: 29 },
  { surface: 'boat', firstMission: 30, lastMission: 34 },
  { surface: 'pixelpast', firstMission: 35, lastMission: 40 },
  { surface: 'kendrex', firstMission: 41, lastMission: 46 },
  { surface: 'autobahn', firstMission: 47, lastMission: 53 },
  { surface: 'productionhell', firstMission: 54, lastMission: 60 },
  { surface: 'awakening', firstMission: 61, lastMission: 68 },
  { surface: 'core', firstMission: 69, lastMission: 73 },
];

const ELIGIBLE_SURFACES = new Set([
  'arrival',
  'cave',
  'desert',
  'fair',
  'lavender',
  'boat',
  'pixelpast',
  'kendrex',
  'autobahn',
  'productionhell',
  'awakening',
]);

const BOSS_MISSION_INDICES = new Set([10, 16, 23, 27, 33, 40, 46, 53, 59, 68, 73, 74, 75]);

/**
 * @param {number} firstMission
 * @param {number} lastMission
 * @returns {number|null}
 */
export function getBossMissionForMissionRange(firstMission, lastMission) {
  const bosses = [...BOSS_MISSION_INDICES].filter(
    (idx) => idx >= firstMission && idx <= lastMission
  );
  if (bosses.length === 0) return null;
  return bosses[bosses.length - 1];
}

export function isAlternativePathEligibleSurface(surface) {
  return ELIGIBLE_SURFACES.has(surface);
}

export function getSurfaceForMissionIndex(missionIndex) {
  const idx = Number(missionIndex);
  const row = SURFACE_MISSION_SLICES.find((s) => idx >= s.firstMission && idx <= s.lastMission);
  return row ? row.surface : null;
}

/** Shared first mission on a surface is never an easy mission. Keep aligned with server. */
export function filterAltPathEasyMissionIndices(surface, easyMissionIndices) {
  const row = SURFACE_MISSION_SLICES.find((s) => s.surface === surface);
  if (!row) {
    return (easyMissionIndices || []).map(Number);
  }
  return (easyMissionIndices || []).map(Number).filter((idx) => idx !== row.firstMission);
}

export function resolveActiveRouteForSurface(surface, plan, routeChoices) {
  const row = SURFACE_MISSION_SLICES.find((s) => s.surface === surface);
  if (!row) {
    throw new Error(`resolveActiveRouteForSurface: unknown surface (${String(surface)})`);
  }
  const route = routeChoices?.[surface] || 'normal';
  if (route === 'normal' || !ELIGIBLE_SURFACES.has(surface)) {
    const missionSequence = [];
    for (let m = row.firstMission; m <= row.lastMission; m += 1) {
      missionSequence.push(m);
    }
    return { route: 'normal', missionSequence, easyMissionIndices: [], missionsRemoved: 0 };
  }
  const surfacePlan = plan?.[surface];
  if (!surfacePlan?.missionSequence) {
    const missionSequence = [];
    for (let m = row.firstMission; m <= row.lastMission; m += 1) {
      missionSequence.push(m);
    }
    return { route: 'normal', missionSequence, easyMissionIndices: [], missionsRemoved: 0 };
  }
  const canonical = [];
  for (let m = row.firstMission; m <= row.lastMission; m += 1) {
    canonical.push(m);
  }
  const missionsRemoved = Number(surfacePlan.missionsRemoved) === 2 ? 2 : 1;
  return {
    route: 'alternative',
    missionSequence: coerceRunPlanMissionSequence(
      canonical,
      missionsRemoved,
      surfacePlan.missionSequence
    ),
    easyMissionIndices: filterAltPathEasyMissionIndices(
      surface,
      surfacePlan.easyMissionIndices || []
    ),
    missionsRemoved: Number(surfacePlan.missionsRemoved) || 0,
  };
}

/**
 * @param {number[]} completedIndices
 * @param {number|null} nextAvailable
 * @param {number[]} missionIndicesPerPoi - global index per displayed POI
 * @returns {('done'|'available'|'unavailable')[]}
 */
export function computeMapSlicePointStatesForRoute(
  completedIndices,
  nextAvailable,
  missionIndicesPerPoi
) {
  if (!Array.isArray(completedIndices)) {
    throw new Error('computeMapSlicePointStatesForRoute: completedIndices must be an array');
  }
  if (!Array.isArray(missionIndicesPerPoi) || missionIndicesPerPoi.length === 0) {
    throw new Error('computeMapSlicePointStatesForRoute: missionIndicesPerPoi is required');
  }
  const completed = new Set(completedIndices);
  return missionIndicesPerPoi.map((globalIdx) => {
    if (completed.has(globalIdx)) return 'done';
    if (nextAvailable === globalIdx) return 'available';
    return 'unavailable';
  });
}

export function isBossMissionIndex(missionIndex) {
  return BOSS_MISSION_INDICES.has(Number(missionIndex));
}

export function needsRouteChoiceForSurface(surface, routeChoices, firstMissionIndex, completedIndices) {
  if (!ELIGIBLE_SURFACES.has(surface)) return false;
  if (routeChoices?.[surface]) return false;
  const completed = new Set(completedIndices || []);
  return completed.has(firstMissionIndex);
}

/** First mission index in `missionIndices` not present in `completedIndices`. */
export function resolveNextMissionInSequence(completedIndices, missionIndices) {
  if (!Array.isArray(missionIndices) || missionIndices.length === 0) {
    return null;
  }
  const completed = new Set(completedIndices || []);
  for (const missionIndex of missionIndices) {
    if (!completed.has(missionIndex)) {
      return missionIndex;
    }
  }
  return null;
}

/** Keep aligned with modules/exploreUnknownAlternativePath.js */
export function isMissionRequiredOnActiveRun(missionIndex, plan, routeChoices) {
  const idx = Number(missionIndex);
  const surface = getSurfaceForMissionIndex(idx);
  if (!surface || !ELIGIBLE_SURFACES.has(surface)) {
    return true;
  }
  const row = SURFACE_MISSION_SLICES.find((s) => s.surface === surface);
  if (!row) return true;
  if (idx < row.firstMission || idx > row.lastMission) {
    return true;
  }
  if (routeChoices?.[surface] !== 'alternative') {
    return true;
  }
  const surfacePlan = plan?.[surface];
  if (!surfacePlan) {
    return true;
  }
  const canonical = [];
  for (let m = row.firstMission; m <= row.lastMission; m += 1) canonical.push(m);
  const missionsRemoved = Number(surfacePlan.missionsRemoved) === 2 ? 2 : 1;
  const plannedSequence = Array.isArray(surfacePlan.missionSequence)
    ? surfacePlan.missionSequence
    : [];
  const sequence = coerceRunPlanMissionSequence(
    canonical,
    missionsRemoved,
    plannedSequence
  );
  return sequence.includes(idx);
}

/** Keep aligned with modules/exploreUnknownAlternativePath.js */
/**
 * @param {number[]} normalMissionIndices
 * @param {1|2} missionsRemoved
 * @param {number[]} seq
 * @returns {boolean}
 */
function isValidPlannedAlternativeSequence(normalMissionIndices, missionsRemoved, seq) {
  const first = normalMissionIndices[0];
  const last = normalMissionIndices[normalMissionIndices.length - 1];
  const boss = getBossMissionForMissionRange(first, last);
  const hasPostBossFinale = boss != null && last !== boss;
  const expectedLen = normalMissionIndices.length - missionsRemoved;
  if (seq.length !== expectedLen) {
    return false;
  }
  const normalSet = new Set(normalMissionIndices);
  if (!seq.every((m) => normalSet.has(m))) {
    return false;
  }
  if (!seq.includes(first) || !seq.includes(boss)) {
    return false;
  }
  if (hasPostBossFinale) {
    if (!seq.includes(last)) {
      return false;
    }
    if (seq.indexOf(last) !== seq.indexOf(boss) + 1) {
      return false;
    }
  }
  const kept = new Set(seq);
  const dropped = normalMissionIndices.filter((m) => !kept.has(m));
  if (dropped.length !== missionsRemoved) {
    return false;
  }
  if (dropped.includes(first) || dropped.includes(boss)) {
    return false;
  }
  if (hasPostBossFinale && dropped.includes(last)) {
    return false;
  }
  return true;
}

/**
 * @param {number[]} normalMissionIndices
 * @param {1|2} missionsRemoved
 * @param {number[]} plannedSequence
 * @returns {number[]}
 */
export function coerceRunPlanMissionSequence(normalMissionIndices, missionsRemoved, plannedSequence) {
  const derived = deriveAlternativeMissionIndices(normalMissionIndices, missionsRemoved);
  if (!Array.isArray(plannedSequence) || plannedSequence.length === 0) {
    return derived;
  }
  const seq = plannedSequence.map(Number);
  if (isValidPlannedAlternativeSequence(normalMissionIndices, missionsRemoved, seq)) {
    return seq;
  }
  return derived;
}

/**
 * @param {{ normalPoints?: number[][], missionIndices?: number[], points?: number[][] }} baseLayout
 * @param {string} surface
 * @param {Record<string, object>} plan
 * @param {1|2} missionsRemoved
 * @param {number[]} canonicalMissionIndices
 */
export function applyRunPlanToAltLayout(
  baseLayout,
  surface,
  plan,
  missionsRemoved,
  canonicalMissionIndices
) {
  const surfacePlan = plan?.[surface];
  const planRemoved = surfacePlan?.missionsRemoved === 2 ? 2 : 1;
  if (
    planRemoved !== missionsRemoved ||
    !Array.isArray(surfacePlan?.missionSequence) ||
    !Array.isArray(baseLayout.normalPoints)
  ) {
    return baseLayout;
  }
  const sequence = coerceRunPlanMissionSequence(
    canonicalMissionIndices,
    missionsRemoved,
    surfacePlan.missionSequence
  );
  const built = buildCompactAlternativeLayout(
    surface,
    baseLayout.normalPoints,
    canonicalMissionIndices,
    sequence
  );
  return { ...baseLayout, ...built };
}

/**
 * Alt layout for a surface: portal POIs + mission indices from the run plan when variant matches.
 * @param {string} surface
 * @param {Record<string, object>} plan
 * @param {1|2} missionsRemoved
 * @param {number} firstMissionIndex
 * @param {number[]} canonicalMissionIndices
 * @param {(surface: string, removed: 1|2, first: number, normals: number[]) => object} getAltLayout
 */
export function resolveAltLayoutForSurface(
  surface,
  plan,
  missionsRemoved,
  firstMissionIndex,
  canonicalMissionIndices,
  getAltLayout
) {
  const surfacePlan = plan?.[surface];
  const layout = getAltLayout(surface, missionsRemoved, firstMissionIndex, canonicalMissionIndices);
  const planRemoved = surfacePlan?.missionsRemoved === 2 ? 2 : 1;
  const plannedSequence = surfacePlan?.missionSequence;
  if (
    planRemoved === missionsRemoved &&
    Array.isArray(plannedSequence) &&
    plannedSequence.length >= 2 &&
    Array.isArray(layout.normalPoints)
  ) {
    const sequence = coerceRunPlanMissionSequence(
      canonicalMissionIndices,
      missionsRemoved,
      plannedSequence
    );
    const built = buildCompactAlternativeLayout(
      surface,
      layout.normalPoints,
      canonicalMissionIndices,
      sequence
    );
    return { ...layout, ...built };
  }
  return layout;
}

export function resolveNextAvailableForRun(completedIndices, plan, routeChoices, missionCount = 75) {
  const completed = new Set(completedIndices);
  for (let m = 1; m <= missionCount; m += 1) {
    if (completed.has(m)) continue;
    if (!isMissionRequiredOnActiveRun(m, plan, routeChoices)) continue;
    return m;
  }
  return null;
}
